/**
 * Executa um conjunto de fetches em paralelo e resolve quando todos
 * terminarem (sucesso ou erro — cada `fetchAsyncState` já captura o próprio
 * erro internamente, então nunca rejeita).
 *
 * Não agrega dados: cada chamada já escreve no seu próprio AsyncState.
 * Serve apenas como ponto de sincronização.
 */
export async function runParallelFetches(fetches: Array<() => Promise<void>>): Promise<void> {
  await Promise.all(fetches.map((fn) => fn()))
}

/**
 * Executa um conjunto de fetches com um teto de concorrência.
 *
 * Ao contrário de dividir em lotes fixos (`[a,b,c]` → aguarda os 3 → `[d,e]`),
 * aqui cada "worker" pega o próximo item da fila assim que libera — não há
 * espera pelo mais lento de um grupo artificial. Com `limit: 3`, no máximo 3
 * fetches ficam em voo a qualquer momento; assim que um termina, o próximo da
 * fila começa imediatamente.
 *
 * `limit` maior que `fetches.length` equivale a `runParallelFetches`.
 */
export async function runWithConcurrencyLimit(
  fetches: Array<() => Promise<void>>,
  limit: number,
): Promise<void> {
  const queue = [...fetches]

  async function worker(): Promise<void> {
    while (queue.length > 0) {
      const fn = queue.shift()
      if (fn) await fn()
    }
  }

  const workerCount = Math.min(limit, fetches.length)

  await Promise.all(Array.from({ length: workerCount }, worker))
}
